import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator, Plus, Trash2, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { LANGUAGES } from '../constants/languages';
import './RateSettingsSection.css';

const DEFAULT_CHARS_PER_PAGE = 1800;
const LANG_OPTIONS = Object.entries(LANGUAGES)
  .map(([code, { name, flag }]) => ({ code, name, label: `${flag} ${name}` }))
  .sort((a, b) => a.name.localeCompare(b.name));

function emptyRow() {
  return { source_lang: 'uk', target_lang: 'en', price: '' };
}

// The translator's own client rates, used only by the frontend price
// calculator. Nothing here is billed - it's their pricing, for their quotes.
//
// `showBasis={false}` hides the source/target basis dropdown, used by the
// Calculator page (single-doc quote has no target - the basis is always
// source). Existing saved value is preserved through save.
function RateSettingsSection({ showBasis = true }) {
  const { t } = useTranslation('settings');
  const { user, updateUser } = useAuth();

  const initial = user?.rate_settings;
  const [currency, setCurrency] = useState(initial?.currency || '');
  const [charsPerPage, setCharsPerPage] = useState(initial?.chars_per_page || DEFAULT_CHARS_PER_PAGE);
  const [basis, setBasis] = useState(initial?.basis === 'target' ? 'target' : 'source');
  const [unit, setUnit] = useState(initial?.unit === 'word' ? 'word' : 'page');
  const [rates, setRates] = useState(
    initial?.rates?.length
      ? initial.rates.map((r) => ({ ...r, price: String(r.price) }))
      : [emptyRow()]
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const sectionRef = useRef(null);

  // The price panel links here with #rates - bring the section into view.
  useEffect(() => {
    if (window.location.hash === '#rates' && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const updateRow = (i, field, value) => {
    setRates((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  };
  const addRow = () => setRates((prev) => [...prev, emptyRow()]);
  const removeRow = (i) => setRates((prev) => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Drop incomplete rows; keep the rest as clean numbers.
      const cleanRates = rates
        .filter((r) => r.source_lang && r.target_lang && r.price !== '' && Number(r.price) >= 0)
        .map((r) => ({
          source_lang: r.source_lang,
          target_lang: r.target_lang,
          price: Number(r.price),
        }));
      const updated = await api.updateUser(user.id, {
        rate_settings: {
          currency: currency.trim(),
          chars_per_page: Number(charsPerPage) || DEFAULT_CHARS_PER_PAGE,
          basis,
          unit,
          rates: cleanRates,
        },
      });
      updateUser(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save rates:', error);
      alert(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-section" id="rates" ref={sectionRef}>
      <div className="section-header">
        <Calculator size={20} />
        <h2>{t('rates.title')}</h2>
      </div>
      <div className="section-content">
        <p className="form-hint">{t('rates.hint')}</p>

        <div className="rate-globals">
          <div className="form-group">
            <label>{t('rates.currency')}</label>
            <input
              className="input"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              placeholder={t('rates.currencyPlaceholder')}
              maxLength={8}
            />
          </div>
          <div className="form-group">
            <label>{t('rates.unit')}</label>
            <select className="input" value={unit} onChange={(e) => setUnit(e.target.value)}>
              <option value="page">{t('rates.unitPage')}</option>
              <option value="word">{t('rates.unitWord')}</option>
            </select>
          </div>
          {unit === 'page' && (
            <div className="form-group">
              <label>{t('rates.charsPerPage')}</label>
              <input
                className="input"
                type="number"
                min={1}
                value={charsPerPage}
                onChange={(e) => setCharsPerPage(e.target.value)}
              />
              <p className="form-hint">{t('rates.charsPerPageHint')}</p>
            </div>
          )}
          {showBasis && (
            <div className="form-group">
              <label>{t('rates.basis')}</label>
              <select className="input" value={basis} onChange={(e) => setBasis(e.target.value)}>
                <option value="source">{t('rates.basisSource')}</option>
                <option value="target">{t('rates.basisTarget')}</option>
              </select>
            </div>
          )}
        </div>

        <div className="rate-rows">
          <div className="rate-row rate-row-head">
            <span>{t('rates.from')}</span>
            <span>{t('rates.to')}</span>
            <span>{unit === 'word' ? t('rates.pricePerWord') : t('rates.pricePerPage')}</span>
            <span />
          </div>
          {rates.map((row, i) => (
            <div className="rate-row" key={i}>
              <select
                className="input"
                value={row.source_lang}
                onChange={(e) => updateRow(i, 'source_lang', e.target.value)}
              >
                {LANG_OPTIONS.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </select>
              <select
                className="input"
                value={row.target_lang}
                onChange={(e) => updateRow(i, 'target_lang', e.target.value)}
              >
                {LANG_OPTIONS.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </select>
              <input
                className="input"
                type="number"
                min={0}
                step="0.001"
                value={row.price}
                onChange={(e) => updateRow(i, 'price', e.target.value)}
                placeholder="0"
              />
              <button
                type="button"
                className="btn btn-ghost btn-icon rate-remove"
                onClick={() => removeRow(i)}
                title={t('rates.remove')}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        <button type="button" className="btn btn-secondary rate-add" onClick={addRow}>
          <Plus size={16} />
          {t('rates.addRow')}
        </button>

        <button className="btn btn-primary rate-save" onClick={handleSave} disabled={saving}>
          {saving ? <div className="spinner" /> : <Save size={16} />}
          {saved ? t('common:status.saved') : t('rates.save')}
        </button>
      </div>
    </section>
  );
}

export default RateSettingsSection;
